import { z } from 'zod';

import { RsvpStatusSchema } from './enums.js';
import type { Role, RsvpStatus } from './enums.js';

/**
 * RSVP and availability (BUILD_PLAN 4.6).
 *
 * Three states and an optional note. A guardian answers for a player, never
 * for themselves: a family with two children on the team answers twice.
 */

/** What a guardian, or a coach on a family's behalf, sends for one player. */
export const RsvpWriteSchema = z.object({
  status: RsvpStatusSchema,
  /** A few words for the coach: "leaving at half time". Empty means none. */
  note: z.string().trim().max(300).optional(),
});
export type RsvpWrite = z.infer<typeof RsvpWriteSchema>;

/**
 * Who sees the roll-up grid, overrides a family's answer, and sends the nudge.
 *
 * BUILD_PLAN section 3, read as the owner reads it: complete. Team Manager's
 * row says "RSVP roll-up" in as many words. Head Coach holds "everything
 * except delete team and transfer ownership", and Owner everything.
 *
 * Assistant Coach is in since ADR-0108: the owner decided on 22 and 24
 * October 2026 that the head coach and the assistant coach hold one
 * permission set. That reverses the 11 October reading recorded in
 * ADR-0067, which had left the assistant out because section 3's row did not
 * list the roll-up.
 *
 * Scorekeeper is absent for the reason the schedule gives: that role can be a
 * stranger holding a texted link, and the roll-up names every child on the
 * team and who is bringing them.
 *
 * There is deliberately NO set for "own RSVPs". ADR-0020: a parent-coach is
 * stored as their staff role and their parenthood is a `guardianships` row,
 * with effective permissions the UNION of the two. So every role may answer
 * for a child it holds a live guardianship for, and the guardianship row is
 * the whole control: a role with none sees nobody and writes for nobody. A
 * set here of `['guardian']` was the first version, and it refused an
 * assistant coach their own child before the guardianship was ever read,
 * which a fresh-context pass graded S1 against ADR-0020.
 */
export const RSVP_ROLLUP: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
]);

export function canReadRsvpRollup(roles: readonly Role[]): boolean {
  return roles.some((role) => RSVP_ROLLUP.has(role));
}

/**
 * The roles that can possibly see coach availability (ADR-0142), for the
 * screen to decide whether to ASK. A hint, never the gate: the server decides
 * from the person's own seat and grants, because a division head or club
 * owner acts under a role that this set cannot tell from a coach, and a
 * team manager who holds the roll-up above is deliberately not here.
 */
export const COACH_AVAILABILITY_ROLES: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
]);

export function mayAskForCoachAvailability(roles: readonly Role[]): boolean {
  return roles.some((role) => COACH_AVAILABILITY_ROLES.has(role));
}

const STATUS_LABEL: Readonly<Record<RsvpStatus, string>> = {
  going: 'Going',
  not_going: 'Not going',
  maybe: 'Maybe',
};

/**
 * The one place a status is spelled for a person.
 *
 * The grid, the schedule row, the parent's home screen and the nudge email all
 * say the same word for the same state, or a family reads "Going" in one place
 * and "Yes" in another and asks which one the coach saw.
 */
export function rsvpStatusLabel(status: RsvpStatus): string {
  return STATUS_LABEL[status];
}
